import React, { useEffect, useMemo, useState } from 'react'

import { Controller, useForm } from 'react-hook-form'

import type { Product } from 'types'

import { Stack, Typography } from '@mui/material'
import { PencilIcon, UploadIcon } from 'lucide-react'

import { Alert, Button, Input, Modal, PhoneField } from '@repo/ui'
import { ProductSelectField, ImportModal, FormSubmitButton } from 'components'
import { useCurrentCompany } from 'hooks'
import { useProducts, useRecordSale } from 'hooks/api'
import { useSnackbar } from 'contexts'

import { MethodOption, BackRow } from '../AddModal/shared'

interface RecordSaleModalProps {
  open: boolean
  onClose: () => void
  refetch: () => void
  /**
   * False when the user lacks `record_sale`. The trigger that opens this is
   * already disabled in that case; this is the defensive half so the submit
   * explains itself rather than answering 403 (§46).
   */
  canRecord?: boolean
  /**
   * VRG-455 — seed the product when opened from a product's own kebab, where
   * the brand is already looking at the thing they sold. Skips the hub and
   * lands straight on the manual form: the method choice has already been made
   * by WHICH entry point they used, and re-asking would be a step that carries
   * no information.
   *
   * Undefined from the /sales page, which opens on the hub as before.
   */
  seedProduct?: Product | null
}

/** Deliberately permissive, and it MIRRORS the server's own check rather than
 *  tightening it. Over-strict email validation rejects real addresses, and
 *  refusing a real buyer's address is a worse failure here than accepting a
 *  typo: a typo is recoverable, a refused sale is not recorded at all. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type RecordSaleForm = {
  product: Product | null
  buyerEmail: string
  buyerPhone: string
}

const EMPTY: RecordSaleForm = {
  product: null,
  buyerEmail: '',
  buyerPhone: '',
}

/**
 * VRG-455 — record that this brand sold this product to this buyer, without
 * naming a certificate.
 *
 * ## What is deliberately NOT asked for
 *
 * A certificate. That is the whole feature: the brand may never have handled
 * the item (dropship, wholesale, outsourced fulfilment), so requiring them to
 * pick one refuses exactly the sales this exists to record. The buyer's own
 * scan later resolves which physical item they hold and binds that tag's
 * certificate.
 *
 * ## Email required, phone required-WHEN-AVAILABLE
 *
 * The email is the identity anchor and is strictly required — a record with no
 * email can never be claimed by anyone. The phone is a second anchor that
 * narrows the design's expected top failure (a buyer who ordered with a work
 * address and signed up with a personal one), but it NEVER blocks submission: a
 * wholesale seller often holds an email and nothing else, and a field that
 * refuses the sale when the channel never collected a number would refuse the
 * sellers this feature is for.
 */
export const RecordSaleModal = (props: RecordSaleModalProps) => {
  const { open, onClose, refetch, canRecord = true, seedProduct = null } = props

  const [mode, setMode] = useState<'hub' | 'manual'>('hub')
  const [openImportModal, setOpenImportModal] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { open: notification } = useSnackbar()
  const { company } = useCurrentCompany()
  const companyId = company?.id ?? null

  const recordSale = useRecordSale()

  const methods = useForm<RecordSaleForm>({
    defaultValues: EMPTY,
    /*
     * VALIDATE ON SUBMIT, then live once they are fixing it.
     *
     * `onChange` marked an email invalid on the SECOND KEYSTROKE — typing "sf"
     * on the way to a real address produced "Enter a valid email address" in red
     * before anyone could have finished. Validation that fires while you are
     * still mid-word is not help, it is a form arguing with you.
     *
     * `reValidateMode: 'onChange'` is the other half and matters as much: once a
     * field HAS errored, corrections clear it immediately rather than making the
     * user submit again to find out they fixed it.
     *
     * ⚠️ TRADE-OFF, stated because it relaxes a standing rule: with `onSubmit`
     * there are no errors before the first attempt, so the submit cannot be
     * disabled-on-error the way `disable-save-on-field-error` describes. It is
     * still gated on `isDirty` (§54) — dead until you type — but an invalid
     * address now reaches a click, and the click is what reports it. That IS the
     * requested behaviour ("after they have input something and tried to click
     * the primary button"), and it is the common pattern; noted so the next
     * reader knows it was chosen rather than overlooked.
     */
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })
  const { control, handleSubmit, reset } = methods

  // The catalog to pick from. Held until the company resolves: products are
  // scoped to a company, so asking early returns an empty picker that looks
  // like an empty catalog.
  const { data: productsData } = useProducts(
    { limit: 200, company_id: companyId ?? undefined },
    { enabled: open && mode === 'manual' && !!companyId },
  )
  const products = useMemo(() => productsData?.data ?? [], [productsData?.data])

  // `reset`, not a run of `setValue` — the submit's dirty gate is computed
  // against `defaultValues`, and setValue moves the values without moving that
  // baseline, which reports the form dirty the moment it is seeded and leaves
  // the gate permanently open (§54).
  useEffect(() => {
    if (open) return
    setMode('hub')
    setOpenImportModal(false)
    setError(null)
    reset(EMPTY)
    recordSale.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // VRG-455 — opened from a product's kebab: seed that product and skip the hub.
  //
  // `reset` rather than `setValue` for the same reason the teardown above uses
  // it: the seeded product must become the form's BASELINE, not a change
  // against an empty one. With setValue the form would report dirty on open and
  // the submit gate would be live before the brand had typed anything (§54).
  useEffect(() => {
    if (!open || !seedProduct) return
    reset({ ...EMPTY, product: seedProduct })
    setMode('manual')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seedProduct])

  const onSubmit = handleSubmit(values => {
    if (!values.product?.id) return
    setError(null)

    recordSale.mutate(
      {
        productId: values.product.id,
        buyerEmail: values.buyerEmail.trim(),
        buyerPhone: values.buyerPhone.trim() || undefined,
      },
      {
        onSuccess: () => {
          // The new row is not visible behind this modal (the list is filtered
          // and paged), so the result needs saying — the toast policy asks for
          // one exactly when the outcome is invisible.
          notification({
            type: 'success',
            title: `Sale recorded and certificate assigned for ${values.buyerEmail.trim()}`,
          })
          refetch()
          onClose()
        },
        // Inline, not a toast: this is the answer to the submit the user just
        // pressed and belongs next to the fields it refused.
        onError: (mutationError: Error) => {
          setError(mutationError?.message || 'Could not record the sale')
        },
      },
    )
  })

  const handleClose = () => {
    setError(null)
    onClose()
  }

  return (
    <Modal
      title="Assign to buyer"
      open={open}
      onClose={handleClose}
      actions={
        mode === 'manual' && !openImportModal ? (
          <>
            {/* Cancel stays enabled: on an untouched form it means "leave", not
                "discard", and disabling it strands the user (§54). */}
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <FormSubmitButton
              control={control}
              type="submit"
              form="record-sale-form"
              loading={recordSale.isPending}
              disabled={!canRecord || recordSale.isPending || !companyId}
              disabledReason={
                canRecord
                  ? undefined
                  : "You need the 'Assign to buyer' permission. Ask an admin to grant it."
              }
            >
              Assign to buyer
            </FormSubmitButton>
          </>
        ) : undefined
      }
    >
      {/* HUB — one sale, or a file */}
      {mode === 'hub' && !openImportModal && (
        <Stack spacing={3}>
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Assign a product to the person who bought it, without picking a
            certificate. Their own scan works out which item they have and
            completes the transfer.
          </Typography>
          <MethodOption
            icon={<PencilIcon strokeWidth={1} size={20} />}
            title="Assign one buyer"
            description="Enter the product and the buyer yourself."
            onClick={() => setMode('manual')}
          />
          <MethodOption
            icon={<UploadIcon strokeWidth={1} size={20} />}
            title="Import a file"
            description="Record many sales at once from a CSV."
            onClick={() => setOpenImportModal(true)}
          />
        </Stack>
      )}

      {/* MANUAL — one product, one buyer */}
      {mode === 'manual' && !openImportModal && (
        <Stack spacing={6}>
          <BackRow onBack={() => setMode('hub')} />

          {!companyId && (
            // Products are scoped to a company, so with none resolved there is
            // nothing to pick. Said plainly rather than showing an empty picker
            // that reads as an empty catalog.
            <Alert
              severity="warning"
              description="Your company is still loading, so there are no products to pick from yet."
              sx={{ padding: '12px 24px' }}
            />
          )}

          {error && (
            <Alert severity="error" description={error} sx={{ padding: '12px 24px' }} />
          )}

          <Stack
            component="form"
            id="record-sale-form"
            onSubmit={onSubmit}
            spacing={6}
            /*
             * `noValidate` — the browser's OWN validation must not run.
             *
             * The email input is `type="email"`, so on submit Chrome performs
             * native constraint validation FIRST and refuses to fire the submit
             * event at all when it fails. React Hook Form therefore never ran,
             * no error rendered, and the button appeared to do nothing — which is
             * what pressing it with "sf" in the field actually did.
             *
             * It was invisible under the old `mode: 'onChange'` because RHF had
             * already painted the error long before anyone clicked. Moving
             * validation to submit is what exposed it.
             */
            noValidate
          >
            {/* WHAT THIS DOES, above the first field. The hub screen explains it,
                but arriving from a product's kebab SKIPS the hub entirely — so
                that path landed straight on "Product" with no statement of what
                assigning a buyer means. The mechanism is not self-evident from
                the fields: nothing here mentions that no certificate is chosen,
                or that the buyer's own scan is what completes it. */}
            <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              Assign a product to the person who bought it, without picking a
              certificate. When they scan the tag on their item, Vera works out
              which one is theirs and transfers it to them automatically.
            </Typography>

            <Controller
              name="product"
              control={control}
              rules={{ required: 'Pick the product that was sold' }}
              render={({ field, fieldState }) => (
                <Stack spacing={2}>
                  {/* The shared picker — image, title and SKU per row, and
                      search, because a catalog can run to hundreds. */}
                  <ProductSelectField
                    products={products}
                    value={field.value}
                    onChange={product => {
                      setError(null)
                      field.onChange(product)
                    }}
                  />
                  {fieldState.error && (
                    <Typography variant="body4" color="error">
                      {fieldState.error.message}
                    </Typography>
                  )}
                </Stack>
              )}
            />

            <Controller
              name="buyerEmail"
              control={control}
              rules={{
                required: "The buyer's email is required",
                pattern: {
                  value: EMAIL_RE,
                  message: 'Enter a valid email address',
                },
              }}
              render={({ field, fieldState }) => (
                // `@repo/ui` Input, so the label and its 8px gap come from
                // FieldLabel rather than a hand-rolled Typography above the
                // control (§31).
                <Input
                  {...field}
                  label="Buyer email"
                  type="email"
                  placeholder="buyer@example.com"
                  helperText={
                    fieldState.error?.message ??
                    'Use the address they ordered with. They can still claim from another verified address on their account.'
                  }
                  error={Boolean(fieldState.error)}
                />
              )}
            />

            {/* The SHARED PhoneField (@repo/ui, added by VRG-490), not a bare
                Input with a regex behind it. It carries the country picker and
                always emits a `+<dial code>`, which is what the API's normalizer
                requires — `normalizePhoneToE164` deliberately refuses to guess a
                country, so a bare national number stored verbatim can never match
                the E.164 the buyer's account resolves to. The regex I had here
                caught that at the door; the field prevents it at the source. */}
            <Controller
              name="buyerPhone"
              control={control}
              render={({ field, fieldState }) => (
                <PhoneField
                  label="Buyer phone"
                  value={field.value}
                  onChange={field.onChange}
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ??
                    'Optional. A verified number gives a second way to match the buyer if they sign up with a different email.'
                  }
                />
              )}
            />

            {/* VRG-455 — the cost, stated where the commitment is made.
                Assigning a buyer commits the brand to a future activation: the
                claim calls `recordActivationUsage` (TD-064 / VRG-163 parity),
                so a charge lands when the buyer scans, not now. The modal
                explained the mechanism and said nothing about money, which is
                the disclosure gap §43 exists to close.

                Conditional, and worded as such: a tag whose certificate is
                already active is not activated again, so a blanket "you will be
                charged" would be false for the re-claim case. */}
            {/*
              A WARNING ALERT, via the shared component — not a hand-rolled icon
              beside a Typography, which is what this was.

              §43 is explicit: a consequence "always renders as a warning Alert",
              and the reasoning there applies exactly here — it was briefly an
              opt-in tier scoped to "irreversible" settings, and the right test
              turned out to be whether HARM ACCRUES that you cannot recover. An
              activation is billed the moment the buyer scans; the sale can be
              voided afterwards but the charge already happened.

              Amber rather than info for the same reason: this is the one thing
              in the modal that costs money, and info-grey reads as a footnote.
            */}
            <Alert
              severity="warning"
              description="When the buyer scans the item and claims it, its certificate activates. That counts as one activation on your plan, unless the tag's certificate is already active. Nothing is charged now."
              sx={{ padding: '12px 24px' }}
            />
          </Stack>
        </Stack>
      )}

      {openImportModal && (
        <ImportModal
          source="sales"
          open={true}
          onClose={() => setOpenImportModal(false)}
          refetch={refetch}
          onImport={() => {
            setOpenImportModal(false)
            handleClose()
          }}
        />
      )}
    </Modal>
  )
}
